import { I18n } from '@calendly/i18n';
import { Button } from '@calendly/ui/components/button';
import { IconAlertSquareRoundedFilled } from '@calendly/ui/components/glyphs/IconAlertSquareRoundedFilled';
import { IconEdit } from '@calendly/ui/components/glyphs/IconEdit';
import { IconPencil } from '@calendly/ui/components/glyphs/IconPencil';
import { IconTrash } from '@calendly/ui/components/glyphs/IconTrash';
import { IconButton } from '@calendly/ui/components/icon-button';
import { BodyText, LabelText } from '@calendly/ui/components/typography';
import { UiIcon } from '@calendly/ui/components/ui-icon';
import { colorIconError } from '@calendly/ui/theme';
import classNames from 'classnames';
import { forwardRef } from 'react';
import { LocationIcon } from 'components/core/location_icon';
import { CONFERENCE_ERROR_CONFIG } from 'components/event_type_edit/location_v3/conference_error';
import { INTEGRATIONS } from 'components/event_type_edit/location_v3/constants';
import { getLabelForKind } from 'components/event_type_edit/location_v3/utils';
import { iconSizeCss } from './styles';
const rowCss = "r1trg5d8";
const rowErrorCss = "rnyy20f";
const rowClickableCss = "r1xoypiz";
const Row = forwardRef((props, ref) => {
  const {
    isError,
    isClickable,
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(rowCss, {
    [rowErrorCss]: isError,
    [rowClickableCss]: isClickable
  }, props.className)} />;
});
const detailsCss = "d1ef8i81";
const Details = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(detailsCss, props.className)} />;
});
const iconBadgeCss = "i1nbw2e4";
const IconBadge = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(iconBadgeCss, props.className)} />;
});
const actionsCss = "a1m01up6";
const Actions = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(actionsCss, props.className)} />;
});
const textColumnCss = "tztr8ld";
const TextColumn = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(textColumnCss, props.className)} />;
});
const ellipsisCss = "ee6e3hr";
const errorTextCss = "e1kkey0z";
const LocationBadgeIcon = ({
  kind,
  showIntegrationError
}) => {
  if (showIntegrationError) {
    return <UiIcon icon={IconAlertSquareRoundedFilled} size="medium" color={colorIconError} title="" className={iconSizeCss} />;
  }
  if (kind === 'custom') {
    return <UiIcon icon={IconEdit} size="medium" title="" className={iconSizeCss} />;
  }
  return <LocationIcon useCrudIcons kind={kind} size="x-large" className={iconSizeCss} />;
};
const getStatusText = (kind, showIntegrationError, subtitle) => {
  if (showIntegrationError) {
    return I18n.t('publisher.event_types.location.conferencing_not_connected', {
      conferencing_name: CONFERENCE_ERROR_CONFIG[kind]?.conferenceName
    });
  }
  if (subtitle) return subtitle;
  return INTEGRATIONS.includes(kind) ? I18n.t('publisher.event_type_editor_crud.meeting_details.location.auto_generated_meeting_link') : null;
};
export const SelectedLocationRow = ({
  kind,
  title,
  subtitle,
  onRemove,
  onRowClick,
  onEdit,
  isReadOnly = false,
  isDisabled = false,
  showIntegrationError = false
}) => {
  const statusText = getStatusText(kind, showIntegrationError, subtitle);
  const showRemove = !isReadOnly && !isDisabled && !!onRemove;
  const showEdit = !isReadOnly && !isDisabled && !!onEdit;
  const showConnect = showIntegrationError && !isReadOnly && !isDisabled;
  const handleRowKeyDown = event => {
    if (event.target !== event.currentTarget) return;
    if (!onRowClick) return;
    if (event.key !== 'Enter' && event.key !== ' ') return;
    event.preventDefault();
    onRowClick();
  };
  const handleRemoveClick = event => {
    event.stopPropagation();
    onRemove();
  };
  const handleEditClick = event => {
    event.stopPropagation();
    onEdit();
  };
  const handleConnectClick = event => {
    event.stopPropagation();
  };
  return <Row data-component="crud-selected-location" isError={showIntegrationError} isClickable={Boolean(onRowClick)} role={onRowClick ? 'button' : undefined} tabIndex={onRowClick ? 0 : undefined} onClick={onRowClick} onKeyDown={onRowClick ? handleRowKeyDown : undefined}>
      <Details>
        <IconBadge>
          <LocationBadgeIcon kind={kind} showIntegrationError={showIntegrationError} />
        </IconBadge>
        <TextColumn>
          <LabelText as="span" size="large" className={ellipsisCss}>
            {title || getLabelForKind(kind)}
          </LabelText>
          {statusText && <BodyText as="span" size="small" color={showIntegrationError ? undefined : 'muted'} className={classNames(ellipsisCss, {
          [errorTextCss]: showIntegrationError
        })}>
              {statusText}
            </BodyText>}
        </TextColumn>
      </Details>
      <Actions>
        {showConnect && <Button as="a" href={CONFERENCE_ERROR_CONFIG[kind]?.link} target="_blank" rel="noopener noreferrer" appearance="secondary-outline" size="small" onClick={handleConnectClick}>
            {I18n.t('publisher.event_type_editor_crud.meeting_details.location.connect_button_label')}
          </Button>}
        {showEdit && <IconButton appearance="ghost" size="small" icon={IconPencil} title={I18n.t('publisher.action.edit')} onClick={handleEditClick} />}
        {showRemove && <IconButton appearance="ghost" size="small" icon={IconTrash} title={I18n.t('publisher.action.remove')} onClick={handleRemoveClick} />}
      </Actions>
    </Row>;
};

require("./selected_location_row.wyw-in-js.css!=!../../../../../../../node_modules/@wyw-in-js/webpack-loader/lib/outputCssLoader.js?cacheProvider=!./selected_location_row.js");